/**
 * Form state for adding one team member.
 *
 * Only what somebody needs to get started: who they are, which clinics they
 * can see, their role, their clinic roles, their clinical titles, how they are
 * employed and their Roger App account. The role pre-checks the clinic roles;
 * both can be changed, and anything finer is set in the User Manager after.
 *
 * Derived rather than typed where it can be: the Roger App username comes from
 * the name and agency code, and whether an app account is made from the role
 * and employment type.
 */

import { useCallback, useMemo, useState } from 'react';
import type { OnboardRogerDocMode, UserArchetype } from '../../../services/api';
import type { UserType } from '../../../services/api/models/UserType';
import type { EmployeeType } from '../../../services/api/models/EmployeeType';
import type { AgencyClinicResponse } from '../types';
import {
  disciplinesForTitles,
  titlesWithoutDiscipline,
} from '../../../utils/clinicalTitles';
import {
  buildRogerAppUsername,
  rogerAppUsernameProblem,
} from '../../../utils/rogerAppUsername';
import { archetypeOption } from './archetypes';
import type { ClinicRoles } from '../grid/clinicSetup';

/**
 * Employment types that do not get a Roger App account by default. Only the
 * explicitly contracted ones -- PRN, per-diem and part-time staff are the
 * agency's own clinicians and do document visits, so treating "not staff" as
 * "no account" would leave them unable to chart.
 */
const EMPLOYMENT_WITHOUT_APP_ACCOUNT = new Set<string>([
  'CONTRACT',
  'CONTRACTOR_PRN',
  'CONTRACTOR_PART_TIME',
]);

/** The common roles, and Other (shown as Custom) for picking roles by hand. */
export type RoleChoice = 'FIELD_CLINICIAN' | 'CLINICAL_SUPERVISOR' | 'OTHER';

/** The preset each role sends; Other sends none and names its roles itself. */
function roleChoiceArchetype(roleChoice: RoleChoice): UserArchetype | null {
  return roleChoice === 'OTHER' ? null : roleChoice;
}

/**
 * What each role pre-checks. Other has no defaults of its own yet. The presets
 * mirror clinic_role_slugs in backend/app/services/user_archetypes.py.
 */
export const ROLE_CHOICE_CLINIC_ROLES: Record<RoleChoice, ClinicRoles> = {
  FIELD_CLINICIAN: {
    clinician: true,
    caseManager: false,
    clinicalManager: false,
  },
  CLINICAL_SUPERVISOR: {
    clinician: true,
    caseManager: true,
    clinicalManager: false,
  },
  OTHER: { clinician: false, caseManager: false, clinicalManager: false },
};

function offersAppAccount(
  roleChoice: RoleChoice,
  clinicRoles: ClinicRoles,
  isAgencyRogerEmrEnabled: boolean
): boolean {
  // An agency with no EMR is precisely a Roger App customer, so there the
  // offer follows the agency rather than the role. Custom made a clinician
  // documents visits too.
  const archetype = roleChoiceArchetype(roleChoice);
  const byRole = archetype
    ? archetypeOption(archetype).offersAppAccount
    : clinicRoles.clinician;
  return byRole || !isAgencyRogerEmrEnabled;
}

/**
 * The app account a role and employment type imply. A deliberate link names a
 * specific account, so it survives while an account is offered at all.
 */
function defaultAppAccountMode(
  roleChoice: RoleChoice,
  clinicRoles: ClinicRoles,
  employeeType: EmployeeType,
  previousMode: OnboardRogerDocMode,
  isAgencyRogerEmrEnabled: boolean
): OnboardRogerDocMode {
  if (!offersAppAccount(roleChoice, clinicRoles, isAgencyRogerEmrEnabled)) {
    return 'none';
  }
  if (previousMode === 'link') return 'link';
  return EMPLOYMENT_WITHOUT_APP_ACCOUNT.has(employeeType) ? 'none' : 'create';
}

export interface OnboardFormState {
  email: string;
  firstName: string;
  lastName: string;
  clinicIds: string[];
  /** Set once the operator edits the clinic list, which stops the seeding. */
  clinicsTouched: boolean;
  roleChoice: RoleChoice;
  /** Agency roles picked by hand under Other; null until edited. */
  otherRoleIds: string[] | null;
  clinicRoles: ClinicRoles;
  userTypes: UserType[];
  employeeType: EmployeeType;
  appAccountMode: OnboardRogerDocMode;
  /** Pre-filled with the derived username; editable. */
  username: string;
  usernameEdited: boolean;
  /** The existing account to attach, when linking. */
  appAccountId: string;
}

const EMPTY: OnboardFormState = {
  email: '',
  firstName: '',
  lastName: '',
  clinicIds: [],
  clinicsTouched: false,
  roleChoice: 'FIELD_CLINICIAN',
  otherRoleIds: null,
  clinicRoles: ROLE_CHOICE_CLINIC_ROLES.FIELD_CLINICIAN,
  userTypes: [],
  employeeType: 'STAFF',
  appAccountMode: 'create',
  username: '',
  usernameEdited: false,
  appAccountId: '',
};

export type OnboardBlockerField =
  'email' | 'clinics' | 'roles' | 'clinicRoles' | 'titles' | 'appAccount';

export interface OnboardBlocker {
  field: OnboardBlockerField;
  message: string;
}

/** A Roger App account already in this agency, as the member list loaded it. */
export interface ExistingAppAccount {
  id: string;
  username: string;
  first_name?: string | null;
  last_name?: string | null;
  linkedToEmail?: string | null;
}

export interface UseOnboardUserOptions {
  clinics: AgencyClinicResponse[];
  agencyAbbreviation: string | null | undefined;
  /** Every Roger App account in the agency, for spotting one that already exists. */
  existingAppAccounts?: readonly ExistingAppAccount[];
  /**
   * Whether the dialog is showing its EMR surface. Without it the presets and
   * clinic roles are about visits the agency does not have, so only Other's
   * hand-picked roles are asked.
   */
  showEmrUserManagement?: boolean;
  /**
   * Whether the agency itself runs the Roger EMR. Kept apart from the surface,
   * which a super user expands: the Roger App offer follows the agency.
   */
  isAgencyRogerEmrEnabled?: boolean;
  /** What Other pre-ticks: the agency's configured Back office roles. */
  customDefaultRoleIds?: readonly string[];
}

const NO_ROLES: readonly string[] = [];

export function useOnboardUser({
  clinics,
  agencyAbbreviation,
  existingAppAccounts = [],
  showEmrUserManagement = true,
  isAgencyRogerEmrEnabled = true,
  customDefaultRoleIds = NO_ROLES,
}: UseOnboardUserOptions) {
  const initial = useMemo((): OnboardFormState => {
    const roleChoice: RoleChoice = showEmrUserManagement
      ? 'FIELD_CLINICIAN'
      : 'OTHER';
    return {
      ...EMPTY,
      roleChoice,
      clinicRoles: ROLE_CHOICE_CLINIC_ROLES[roleChoice],
      appAccountMode: defaultAppAccountMode(
        roleChoice,
        ROLE_CHOICE_CLINIC_ROLES[roleChoice],
        EMPTY.employeeType,
        EMPTY.appAccountMode,
        isAgencyRogerEmrEnabled
      ),
    };
  }, [showEmrUserManagement, isAgencyRogerEmrEnabled]);
  const [form, setForm] = useState<OnboardFormState>(initial);

  const patch = useCallback((changes: Partial<OnboardFormState>) => {
    setForm(previous => ({ ...previous, ...changes }));
  }, []);

  const reset = useCallback(() => setForm(initial), [initial]);

  const roleChoice: RoleChoice = showEmrUserManagement
    ? form.roleChoice
    : 'OTHER';
  const archetype = roleChoiceArchetype(roleChoice);
  const option = archetype ? archetypeOption(archetype) : null;
  const appAccountOffered = offersAppAccount(
    roleChoice,
    form.clinicRoles,
    isAgencyRogerEmrEnabled
  );

  // Every clinic starts checked. Derived rather than seeded into state, so
  // reopening the dialog starts checked again without an effect on the list.
  const effectiveClinicIds = useMemo(
    () =>
      form.clinicsTouched ? form.clinicIds : clinics.map(clinic => clinic.id),
    [clinics, form.clinicsTouched, form.clinicIds]
  );

  // The server derives the same from the titles; shown so nobody wonders.
  const disciplines = useMemo(
    () => disciplinesForTitles(form.userTypes),
    [form.userTypes]
  );
  const uselessTitles = useMemo(
    () => titlesWithoutDiscipline(form.userTypes),
    [form.userTypes]
  );

  // Derived until the operator edits the picker, then theirs.
  const otherRoleIds = form.otherRoleIds ?? [...customDefaultRoleIds];

  // Untouched, the preset's clinic roles are left to the server to derive.
  const defaults = ROLE_CHOICE_CLINIC_ROLES[roleChoice];
  const clinicRolesEdited = (
    Object.keys(defaults) as (keyof ClinicRoles)[]
  ).some(key => form.clinicRoles[key] !== defaults[key]);

  const derivedUsername = useMemo(
    () =>
      buildRogerAppUsername({
        firstName: form.firstName,
        lastName: form.lastName,
        agencyAbbreviation,
      }),
    [form.firstName, form.lastName, agencyAbbreviation]
  );

  // The field shows the derived value until the operator types over it, so the
  // preview keeps up with the name without discarding an edit.
  const username = form.usernameEdited
    ? form.username
    : (derivedUsername ?? '');

  /**
   * An account that looks like this person, found before they submit.
   *
   * Worth surfacing even when it belongs to somebody else: hiding it is what
   * produces "why won't it let me", and the same match is also the reason the
   * derived username is taken -- two people named J. Smith at one agency derive
   * the same one. Read-only either way; re-pointing an account would break the
   * other person's app login, so "same person" has to mean unlinking them first.
   */
  const existingAccountMatch = useMemo(() => {
    const first = form.firstName.trim().toLowerCase();
    const last = form.lastName.trim().toLowerCase();
    if (!first || !last) return null;

    const byName = existingAppAccounts.find(
      account =>
        (account.first_name ?? '').trim().toLowerCase() === first &&
        (account.last_name ?? '').trim().toLowerCase() === last
    );
    const byUsername = derivedUsername
      ? existingAppAccounts.find(
          account => account.username === derivedUsername
        )
      : undefined;

    const match = byName ?? byUsername;
    if (!match) return null;

    return {
      account: match,
      /** Whose account it is, when it already belongs to somebody. */
      linkedToEmail: match.linkedToEmail ?? null,
      /** Same name, so probably the same person. */
      sameName: match === byName,
    };
  }, [form.firstName, form.lastName, derivedUsername, existingAppAccounts]);

  const linkableAccounts = useMemo(
    () => existingAppAccounts.filter(account => !account.linkedToEmail),
    [existingAppAccounts]
  );

  const setEmployeeType = useCallback(
    (employeeType: EmployeeType) => {
      setForm(previous => ({
        ...previous,
        employeeType,
        appAccountMode: defaultAppAccountMode(
          previous.roleChoice,
          previous.clinicRoles,
          employeeType,
          previous.appAccountMode,
          isAgencyRogerEmrEnabled
        ),
      }));
    },
    [isAgencyRogerEmrEnabled]
  );

  const linkExistingAccount = useCallback((accountId: string) => {
    setForm(previous => ({
      ...previous,
      appAccountMode: 'link',
      appAccountId: accountId,
    }));
  }, []);

  const toggleClinic = useCallback(
    (clinicId: string) => {
      setForm(previous => {
        // The first toggle takes the derived all-checked list into state, so
        // unchecking one clinic keeps the rest rather than starting from empty.
        const current = previous.clinicsTouched
          ? previous.clinicIds
          : clinics.map(clinic => clinic.id);
        return {
          ...previous,
          clinicsTouched: true,
          clinicIds: current.includes(clinicId)
            ? current.filter(id => id !== clinicId)
            : [...current, clinicId],
        };
      });
    },
    [clinics]
  );

  // Choosing a role resets the clinic roles to its defaults, and the app
  // account to what that role and the employment type imply.
  const setRoleChoice = useCallback(
    (next: RoleChoice) => {
      setForm(previous => ({
        ...previous,
        roleChoice: next,
        clinicRoles: ROLE_CHOICE_CLINIC_ROLES[next],
        appAccountMode: defaultAppAccountMode(
          next,
          ROLE_CHOICE_CLINIC_ROLES[next],
          previous.employeeType,
          previous.appAccountMode,
          isAgencyRogerEmrEnabled
        ),
      }));
    },
    [isAgencyRogerEmrEnabled]
  );

  // Re-derives the app account only when the offer itself changes, so a
  // choice made for this role survives ticking an unrelated clinic role.
  const toggleClinicRole = useCallback(
    (key: keyof ClinicRoles) => {
      setForm(previous => {
        const clinicRoles = {
          ...previous.clinicRoles,
          [key]: !previous.clinicRoles[key],
        };
        const offerChanged =
          offersAppAccount(
            previous.roleChoice,
            clinicRoles,
            isAgencyRogerEmrEnabled
          ) !==
          offersAppAccount(
            previous.roleChoice,
            previous.clinicRoles,
            isAgencyRogerEmrEnabled
          );
        return {
          ...previous,
          clinicRoles,
          appAccountMode: offerChanged
            ? defaultAppAccountMode(
                previous.roleChoice,
                clinicRoles,
                previous.employeeType,
                previous.appAccountMode,
                isAgencyRogerEmrEnabled
              )
            : previous.appAccountMode,
        };
      });
    },
    [isAgencyRogerEmrEnabled]
  );

  const toggleTitle = useCallback((title: UserType) => {
    setForm(previous => ({
      ...previous,
      userTypes: previous.userTypes.includes(title)
        ? previous.userTypes.filter(t => t !== title)
        : [...previous.userTypes, title],
    }));
  }, []);

  /**
   * Why the form cannot be submitted yet, addressed to the field it is about so
   * the dialog can show it there. Mirrors the backend's refusals so the dialog
   * does not have to send a request to find out; the backend still checks.
   */
  // Anyone who can be put on a visit needs a title that allows one.
  const needsVisitTypes =
    !!option?.needsTitles ||
    (showEmrUserManagement && form.clinicRoles.clinician);

  const blocker = useMemo((): OnboardBlocker | null => {
    if (!form.email.trim()) {
      return { field: 'email', message: 'Enter their work email address.' };
    }
    // The submit button is not a native form submit, so nothing else checks
    // this. Left to the server, the member is written and committed before
    // WorkOS rejects the address, leaving somebody who exists and can never
    // log in.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return {
        field: 'email',
        message: 'That does not look like an email address.',
      };
    }
    // Without a clinic they see no clinic-scoped data at all.
    if (clinics.length > 0 && effectiveClinicIds.length === 0) {
      return {
        field: 'clinics',
        message: option?.needsClinics
          ? 'Choose at least one clinic, otherwise they cannot be scheduled.'
          : 'Choose at least one clinic.',
      };
    }
    // Membership alone shows them nothing once they sign in.
    if (
      roleChoice === 'OTHER' &&
      otherRoleIds.length === 0 &&
      !(showEmrUserManagement && Object.values(form.clinicRoles).some(Boolean))
    ) {
      return {
        field: 'roles',
        message:
          'Choose at least one role, or they will see nothing after they sign in.',
      };
    }
    // The server refuses this too: a preset clinician nobody can schedule.
    if (option?.needsClinics && !form.clinicRoles.clinician) {
      return {
        field: 'clinicRoles',
        message: 'Tick Clinician, or nobody will be able to schedule them.',
      };
    }
    // Somebody in the office holds no title, so only a schedulable person
    // is asked for one.
    if (needsVisitTypes && form.userTypes.length === 0) {
      return {
        field: 'titles',
        message:
          'Choose at least one clinical title, which decides the visits they can take.',
      };
    }
    if (needsVisitTypes && disciplines.length === 0) {
      return {
        field: 'titles',
        message:
          'These titles allow no visit types, so they could not be scheduled.',
      };
    }
    if (form.appAccountMode === 'link' && !form.appAccountId) {
      return {
        field: 'appAccount',
        message: 'Choose the Roger App account to link.',
      };
    }
    if (form.appAccountMode === 'create') {
      const problem = rogerAppUsernameProblem(username);
      if (problem) return { field: 'appAccount', message: problem };
    }
    return null;
  }, [
    form.email,
    form.userTypes,
    form.clinicRoles,
    roleChoice,
    otherRoleIds.length,
    showEmrUserManagement,
    form.appAccountMode,
    form.appAccountId,
    clinics.length,
    effectiveClinicIds,
    option,
    needsVisitTypes,
    disciplines,
    username,
  ]);

  return {
    form,
    patch,
    reset,
    roleChoice,
    archetype,
    appAccountOffered,
    clinicRolesEdited,
    otherRoleIds,
    disciplines,
    uselessTitles,
    derivedUsername,
    username,
    setEmployeeType,
    setRoleChoice,
    toggleClinicRole,
    toggleClinic,
    effectiveClinicIds,
    toggleTitle,
    blocker,
    existingAccountMatch,
    linkableAccounts,
    linkExistingAccount,
  };
}
